import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { formatCurrency, formatDate } from '../utils/helpers';
import { ApprovalBadge, RowActions, PendingApprovalBanner, PendingCountBadge } from '../components/ApprovalActions';

const empty = { vehicle_no:'', vehicle_type:'', model:'', capacity:'', driver_name:'', driver_phone:'', driver_license:'', insurance_expiry:'', fitness_expiry:'', permit_expiry:'', pollution_expiry:'', purchase_date:'', purchase_cost:0, current_km:0, status:'active' };

export default function Vehicles() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get('/vehicles').then(r => setList(r.data.data || []));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.vehicle_no) return toast.error('Vehicle number required');
    try {
      if (editId) await api.put('/vehicles/' + editId, form);
      else await api.post('/vehicles', form);
      toast.success('Saved'); setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(e?.response?.data?.message || 'Error'); }
  };
  const edit = (v) => { setForm({...empty, ...v}); setEditId(v.id); setOpen(true); };

  const pendingCount = list.filter(v => v.approval_status === 'pending').length;
  return (
    <div className="card">
      <div className="card-header">
        <h3>Vehicles / Fleet ({list.length})<PendingCountBadge count={pendingCount}/></h3>
        <button className="btn btn-blue" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }}><Plus size={14}/> Add Vehicle</button>
      </div>
      <div className="table-wrap">
        <table>
          <thead><tr><th>Vehicle No</th><th>Type</th><th>Driver</th><th>Phone</th><th>KM</th><th>Insurance</th><th className="td-num">Maint.</th><th className="td-num">Fuel</th><th>Status</th><th>Approval</th><th>Actions</th></tr></thead>
          <tbody>
            {list.length ? list.map(v => (
              <tr key={v.id} style={v.approval_status==='pending'?{background:'#fffbeb'}:{}}>
                <td><strong>{v.vehicle_no}</strong></td>
                <td>{v.vehicle_type || '-'}</td>
                <td>{v.driver_name || '-'}</td>
                <td>{v.driver_phone || '-'}</td>
                <td>{v.current_km}</td>
                <td>{formatDate(v.insurance_expiry)}</td>
                <td className="td-num">{formatCurrency(v.total_maintenance)}</td>
                <td className="td-num">{formatCurrency(v.total_fuel)}</td>
                <td><span className={'badge ' + (v.status==='active'?'badge-green':v.status==='maintenance'?'badge-orange':'badge-red')}>{v.status}</span></td>
                <td><ApprovalBadge status={v.approval_status} createdByName={v.created_by_name}/></td>
                <td><RowActions endpoint="/vehicles" row={v} onChange={load} onEdit={edit} confirmDeleteMsg="Delete this vehicle?"/></td>
              </tr>
            )) : <tr><td colSpan="11" className="empty">No vehicles</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={()=>setOpen(false)} title={editId?'Edit Vehicle':'Add Vehicle'} size="lg"
        footer={<>
          <button className="btn btn-gray" onClick={()=>setOpen(false)}>Cancel</button>
          <button className="btn btn-blue" onClick={save}>{editId?'Update':'Create'}</button>
        </>}>
        <PendingApprovalBanner isEdit={!!editId}/>
        <div className="form-grid">
          <div className="form-group"><label>Vehicle No *</label><input value={form.vehicle_no} onChange={e=>setForm({...form, vehicle_no:e.target.value.toUpperCase()})}/></div>
          <div className="form-group"><label>Vehicle Type</label>
            <select value={form.vehicle_type} onChange={e=>setForm({...form, vehicle_type:e.target.value})}>
              <option value="">-- Select --</option>
              <option>Truck</option><option>Mini Truck</option><option>Lorry</option><option>Tempo</option><option>Pickup</option><option>Container</option><option>Other</option>
            </select>
          </div>
          <div className="form-group"><label>Model</label><input value={form.model} onChange={e=>setForm({...form, model:e.target.value})}/></div>
          <div className="form-group"><label>Capacity</label><input placeholder="e.g. 10 Ton" value={form.capacity} onChange={e=>setForm({...form, capacity:e.target.value})}/></div>
          <div className="form-group"><label>Driver Name</label><input value={form.driver_name} onChange={e=>setForm({...form, driver_name:e.target.value})}/></div>
          <div className="form-group"><label>Driver Phone</label><input value={form.driver_phone} onChange={e=>setForm({...form, driver_phone:e.target.value})}/></div>
          <div className="form-group"><label>Driver License</label><input value={form.driver_license} onChange={e=>setForm({...form, driver_license:e.target.value})}/></div>
          <div className="form-group"><label>Current Odometer (KM)</label><input type="number" value={form.current_km} onChange={e=>setForm({...form, current_km:e.target.value})}/></div>
          <div className="form-group"><label>Insurance Expiry</label><input type="date" value={form.insurance_expiry||''} onChange={e=>setForm({...form, insurance_expiry:e.target.value})}/></div>
          <div className="form-group"><label>Fitness Expiry</label><input type="date" value={form.fitness_expiry||''} onChange={e=>setForm({...form, fitness_expiry:e.target.value})}/></div>
          <div className="form-group"><label>Permit Expiry</label><input type="date" value={form.permit_expiry||''} onChange={e=>setForm({...form, permit_expiry:e.target.value})}/></div>
          <div className="form-group"><label>Pollution Expiry</label><input type="date" value={form.pollution_expiry||''} onChange={e=>setForm({...form, pollution_expiry:e.target.value})}/></div>
          <div className="form-group"><label>Purchase Date</label><input type="date" value={form.purchase_date||''} onChange={e=>setForm({...form, purchase_date:e.target.value})}/></div>
          <div className="form-group"><label>Purchase Cost</label><input type="number" value={form.purchase_cost} onChange={e=>setForm({...form, purchase_cost:e.target.value})}/></div>
          <div className="form-group"><label>Status</label>
            <select value={form.status} onChange={e=>setForm({...form, status:e.target.value})}>
              <option value="active">Active</option><option value="maintenance">In Maintenance</option><option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </Modal>
    </div>
  );
}
